import { devLog } from '../../utils/devLog';
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { resetAlgoAccess } from '../../pages/Admin/algoDeviceSupport';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { getTenants, logTenantSwitch, getMyMemberships } from '../../api/backendApi';
import { readDealerAdminSession } from '../../pages/Admin/dealerAdminSession';
import { clearSupportedTenantSession, readSupportedTenantSession, subscribeSupportedTenantSession, writeSupportedTenantSession } from '../../pages/Admin/supportedTenantsSession';
import { activeMembershipSupersedesSupport, shouldEnterPlatformSupport } from '../../pages/Admin/platformSupportEntry';

// Create the context
const TenantContext = createContext();

// Default tenant ID to use if all else fails
const DEFAULT_TENANT_ID = '4hSVnGZGvSbyQxZt5gFv';

// Custom hook to use the tenant context
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Provider component
export const TenantProvider = ({ children }) => {
  const { currentUser, role, userClaims, userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipsError, setMembershipsError] = useState(null);
  const initializedRef = useRef(false);

  // Create a default tenant object
  const createDefaultTenant = tenantId => {
    return {
      id: tenantId,
      name: 'Default Tenant',
      createdAt: new Date().toISOString(),
    };
  };

  // Get tenant ID from various sources
  const getTenantId = () => {
    const dealerSession = readDealerAdminSession();
    if (dealerSession?.tenantId) {
      return dealerSession.tenantId;
    }
    // Try to get from localStorage first
    try {
      const storedTenantId = localStorage.getItem('selectedTenantId');
      if (storedTenantId) {
        devLog('TenantContext: Found tenant ID in localStorage:', storedTenantId);
        return storedTenantId;
      }
    } catch (e) {
      console.warn('TenantContext: Error accessing localStorage:', e?.message || 'storage unavailable');
    }

    // Try to get from user claims
    if (currentUser?.tenantId) {
      devLog('TenantContext: Found tenant ID in user claims:', currentUser.tenantId);
      return currentUser.tenantId;
    }

    // Try to get from user claims object
    if (currentUser?.claims?.tenantId) {
      devLog(
        'TenantContext: Found tenant ID in user claims object:',
        currentUser.claims.tenantId
      );
      return currentUser.claims.tenantId;
    }

    // Try to get from custom claims
    if (currentUser?.customClaims?.tenantId) {
      devLog(
        'TenantContext: Found tenant ID in custom claims:',
        currentUser.customClaims.tenantId
      );
      return currentUser.customClaims.tenantId;
    }

    // Try to get from Firestore data
    if (currentUser?.firestoreData?.tenantId) {
      devLog(
        'TenantContext: Found tenant ID in Firestore data:',
        currentUser.firestoreData.tenantId
      );
      return currentUser.firestoreData.tenantId;
    }

    // Use default tenant ID as last resort
    devLog('TenantContext: Using default tenant ID:', DEFAULT_TENANT_ID);
    return DEFAULT_TENANT_ID;
  };

  useEffect(() => {
    const tenantId = selectedTenant?.id || null;
    if (!tenantId) return;
    if (window.electronAPI?.setAgentContext) {
      window.electronAPI.setAgentContext({ tenantId });
    }
  }, [selectedTenant?.id]);

  // Initialize with a default tenant
  useEffect(() => {
    const initializeDefaultTenant = () => {
      const tenantId = getTenantId();
      const defaultTenant = createDefaultTenant(tenantId);

      devLog('TenantContext: Initializing with default tenant:', defaultTenant);
      setSelectedTenant(defaultTenant);
      setAvailableTenants([defaultTenant]);
      // Make tenant immediately usable by consumers
      setLoading(false);

      // Store in localStorage for future use
      try {
        localStorage.setItem('selectedTenantId', tenantId);
      } catch (e) {
        console.warn('TenantContext: Error storing tenant ID in localStorage:', e?.message || 'storage unavailable');
      }
    };

    // Initialize immediately with a default tenant
    initializeDefaultTenant();
    // Mark initialization complete after default tenant is set
    initializedRef.current = true;
  }, []);

  useEffect(() => {
    if (!dealerAdmin.active || !dealerAdmin.tenantId) return;
    setSelectedTenant((prev) => {
      if (prev?.id === dealerAdmin.tenantId) return prev;
      return {
        id: dealerAdmin.tenantId,
        name: prev?.name && prev?.id === dealerAdmin.tenantId ? prev.name : 'Assigned tenant',
        createdAt: prev?.createdAt || new Date().toISOString(),
      };
    });
    try {
      localStorage.setItem('selectedTenantId', dealerAdmin.tenantId);
    } catch {
      // ignore
    }
  }, [dealerAdmin.active, dealerAdmin.tenantId]);

  useEffect(() => subscribeSupportedTenantSession(() => {
    const session = readSupportedTenantSession();
    if (!session?.tenantId || readDealerAdminSession()) return;
    setSelectedTenant({
      id: session.tenantId,
      name: session.name || 'Supported tenant',
      createdAt: new Date().toISOString(),
    });
    try {
      localStorage.setItem('selectedTenantId', session.tenantId);
    } catch {
      // The support header still selects the tenant when storage is blocked.
    }
  }), []);

  // Fetch available tenants when user changes (background; do not block UI)
  useEffect(() => {
    const fetchTenantsData = async () => {
      if (!currentUser) {
        devLog('TenantContext: No current user, skipping tenant fetch');
        return;
      }

      devLog('TenantContext: Fetching tenants for user:', currentUser);
      setError(null);

      try {
        // Use the backendApi getTenants function that works in the tenant management page
        devLog('TenantContext: Calling backendApi.getTenants()');
        const tenantsData = await getTenants();
        devLog('TenantContext: Received tenants from API:', tenantsData);

        if (!tenantsData || tenantsData.length === 0) {
          // If no tenants returned, keep using the default tenant
          console.warn('TenantContext: No tenants returned from API, keeping default tenant');
        } else {
          // We have tenants from the API
          devLog('TenantContext: Setting', tenantsData.length, 'tenants from API');
          setAvailableTenants(tenantsData);

          // Try to select the user's default tenant if it exists in the list
          const dealerTenantId = readDealerAdminSession()?.tenantId || '';
          const supportTenantId = dealerTenantId ? '' : (readSupportedTenantSession()?.tenantId || '');
          const userTenantId = dealerTenantId || supportTenantId || getTenantId();
          const userTenant = tenantsData.find(t => t.id === userTenantId);

          if (dealerTenantId) {
            setSelectedTenant(userTenant || createDefaultTenant(dealerTenantId));
            try {
              localStorage.setItem('selectedTenantId', dealerTenantId);
            } catch (e) {
              console.warn('TenantContext: Error storing tenant ID in localStorage:', e?.message || 'storage unavailable');
            }
          } else if (supportTenantId) {
            const supportName = readSupportedTenantSession()?.name || 'Supported tenant';
            setSelectedTenant(userTenant || { id: supportTenantId, name: supportName, createdAt: new Date().toISOString() });
            try {
              localStorage.setItem('selectedTenantId', supportTenantId);
            } catch (e) {
              console.warn('TenantContext: Error storing tenant ID in localStorage:', e?.message || 'storage unavailable');
            }
          } else if (userTenant) {
            devLog('TenantContext: Selected user tenant:', userTenant);
            setSelectedTenant(userTenant);

            // Store in localStorage for future use
            try {
              localStorage.setItem('selectedTenantId', userTenant.id);
            } catch (e) {
              console.warn('TenantContext: Error storing tenant ID in localStorage:', e?.message || 'storage unavailable');
            }
          } else if (tenantsData.length > 0) {
            // Otherwise select the first tenant
            devLog('TenantContext: Selected first tenant:', tenantsData[0]);
            setSelectedTenant(tenantsData[0]);

            // Store in localStorage for future use
            try {
              localStorage.setItem('selectedTenantId', tenantsData[0].id);
            } catch (e) {
              console.warn('TenantContext: Error storing tenant ID in localStorage:', e?.message || 'storage unavailable');
            }
          }
        }
      } catch (err) {
        console.error('TenantContext: Error fetching tenants:', err?.message || 'request failed');
        setError('Failed to load tenants. Please try again later.');
        // Keep using the default tenant, no need to do anything
      } finally {
        // Ensure consumers that still check `loading` see ready state
        setLoading(false);
      }
    };

    // Fetch tenants immediately in background
    fetchTenantsData();
  }, [currentUser?.uid]);

  // Fetch memberships for the current user (background; do not block UI)
  useEffect(() => {
    const fetchMemberships = async () => {
      if (!currentUser) {
        setMemberships([]);
        return;
      }

      devLog('TenantContext: Fetching memberships for user:', currentUser.uid);
      setMembershipsError(null);

      try {
        setMembershipsLoading(true);
        const items = await getMyMemberships({ includeHidden: false, includeRemoved: false });
        setMemberships(items || []);
      } catch (err) {
        console.error('TenantContext: Error fetching memberships:', err?.message || 'request failed');
        setMembershipsError('Failed to load memberships. Some tenant metadata may be missing.');
      } finally {
        setMembershipsLoading(false);
      }
    };

    fetchMemberships();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (membershipsLoading || membershipsError) return undefined;
    const session = readSupportedTenantSession();
    if (!activeMembershipSupersedesSupport({
      supportTenantId: session?.tenantId,
      memberships,
    })) return undefined;
    clearSupportedTenantSession();
    return undefined;
  }, [memberships, membershipsError, membershipsLoading]);

  useEffect(() => {
    if (membershipsLoading || membershipsError) return undefined;
    const tenantId = userClaims?.tenantId || userData?.tenantId || '';
    const membership = (memberships || []).find((item) => item?.tenantId === tenantId);
    if (!shouldEnterPlatformSupport({
      role,
      tenantId,
      membershipStatus: membership?.status,
      dealerActive: dealerAdmin?.active,
    })) return undefined;
    const known = (availableTenants || []).find((item) => item?.id === tenantId);
    writeSupportedTenantSession({
      tenantId,
      name: known?.name || known?.displayName || userData?.tenantName || '',
      accessLevel: 'manage',
    });
    return undefined;
  }, [
    availableTenants,
    dealerAdmin?.active,
    memberships,
    membershipsError,
    membershipsLoading,
    role,
    userClaims?.tenantId,
    userData?.tenantId,
    userData?.tenantName,
  ]);

  // Custom setSelectedTenant that also updates localStorage
  const handleSetSelectedTenant = useCallback((tenant) => {
    if (tenant) {
      // Determine previous tenant before updating
      let previousTenantId = undefined;
      try {
        previousTenantId = localStorage.getItem('selectedTenantId') || undefined;
      } catch (e) {
        // ignore
      }

      setSelectedTenant(tenant);

      // Store in localStorage for future use
      try {
        localStorage.setItem('selectedTenantId', tenant.id);
        devLog('TenantContext: Stored tenant ID in localStorage:', tenant.id);
      } catch (e) {
        console.warn('TenantContext: Error storing tenant ID in localStorage:', e?.message || 'storage unavailable');
      }

      // Log tenant switch only after initialization and only if it changed
      if (initializedRef.current && previousTenantId && previousTenantId !== tenant.id) {
        resetAlgoAccess();
        (async () => {
          try {
            await logTenantSwitch(tenant.id, previousTenantId);
          } catch (err) {
            console.warn('TenantContext: logTenantSwitch failed (non-blocking):', err?.message || 'request failed');
          }
        })();
      }
    }
  }, []);

  const membershipsByTenantId = {};
  (memberships || []).forEach(m => {
    if (m && m.tenantId) {
      membershipsByTenantId[m.tenantId] = m;
    }
  });

  const selectedMembership = selectedTenant ? membershipsByTenantId[selectedTenant.id] : null;

  const value = {
    selectedTenant,
    setSelectedTenant: handleSetSelectedTenant,
    availableTenants,
    loading,
    error,
    memberships,
    membershipsLoading,
    membershipsError,
    membershipsByTenantId,
    selectedMembership,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export default TenantContext;
